/**
 * "Open in the app" on an iPhone (GH-873, TestFlight build 35, AMyWTqQo: "the webpage should have
 * redirected to the app after login so that I'm logged into the app").
 *
 * Why a link, and why the app's own scheme:
 *
 *  - The camera opens a big-screen code in the app only when iOS holds the association file's
 *    `/c/*?access=` / `?join=` claim for that host, and Safari keeps a per-domain choice: once
 *    someone taps the breadcrumb back to Safari, that host's links open in Safari until they choose
 *    the app again. Neither is visible to this page.
 *  - A universal link tapped on its own domain never leaves Safari — Apple opens the app only for
 *    a link to a *different* domain — so a plain `https://<this host>/c/…` button would do
 *    nothing. The app's own `givehere://open?url=<this link>` always reaches the app, which reads
 *    the inner link exactly as a universal link (host check included, `LinkDispatch`).
 *  - Signing in here does not sign the app in, and never will: a session token in a URL would be a
 *    credential resting in history and logs. The app signs in on its own doors, and the access and
 *    join tokens this link carries are what a scan of the screen would have given it.
 *
 * Drawn only on an iPhone or iPad. Without the app installed the tap shows Safari's "cannot open"
 * notice and nothing else happens, which is why it is a quiet link and never a redirect.
 */

/** The app's own door for a web link (`LinkDispatch.openHost` in the app). */
export const APP_OPEN_PREFIX = "givehere://open?url=";

/** Whether this browser is on an iPhone or iPad (iPadOS reports a Mac, with touch). */
export function isAppleMobile(
  nav: Pick<Navigator, "userAgent" | "maxTouchPoints"> | undefined = typeof navigator ===
  "undefined"
    ? undefined
    : navigator,
): boolean {
  if (!nav) return false;
  if (/\b(iPhone|iPad|iPod)\b/.test(nav.userAgent)) return true;
  return /\bMacintosh\b/.test(nav.userAgent) && (nav.maxTouchPoints ?? 0) > 1;
}

/**
 * The web link the app should open: this host's `/c/<eventId>`, with the event's access token and
 * the big screen's join token when this page holds them. Null without an https origin — the app
 * reads nothing else.
 */
export function appEventLink(input: {
  readonly origin: string | undefined;
  readonly eventId: string;
  readonly accessToken?: string | null;
  readonly joinToken?: string | null;
}): string | null {
  if (!input.origin || !input.origin.startsWith("https://")) return null;
  const url = new URL(`/c/${encodeURIComponent(input.eventId)}`, input.origin);
  if (input.accessToken) url.searchParams.set("access", input.accessToken);
  if (input.joinToken) url.searchParams.set("join", input.joinToken);
  return url.toString();
}

/** The `href` that hands `webLink` to the app. */
export function openInAppHref(webLink: string): string {
  return `${APP_OPEN_PREFIX}${encodeURIComponent(webLink)}`;
}
